/**
 * API Service
 * Centralized service with typed methods for common HTTP operations
 * Handles the API response wrapper format automatically
 */

import { apiClient, createRequestConfig } from './apiClient';
import type { ApiResponse, PaginatedApiResponse, RequestConfig } from './types';

/**
 * Centralized API Service
 * Provides typed methods for common HTTP operations
 */
export const apiService = {
  /**
   * GET request - returns the raw response body (useful when response format varies)
   * Use this when the API response doesn't follow the standard ApiResponse<T> wrapper
   */
  async getRaw<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const axiosConfig = createRequestConfig(config?.params, {
      headers: config?.headers,
      timeout: config?.timeout,
      signal: config?.signal,
    });
    const response = await apiClient.get<T>(endpoint, axiosConfig);
    return response.data;
  },

  /**
   * GET request - returns the full API response (data + meta)
   * Use this when the API follows the standard ApiResponse<T> format
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.getRaw<ApiResponse<T>>(endpoint, config);
  },

  /**
   * GET request - returns only the data portion of the response
   * Use this when you don't need the meta information
   */
  async getData<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const response = await this.get<T>(endpoint, config);
    return response.data;
  },

  /**
   * GET request for paginated responses
   */
  async getPaginated<T>(
    endpoint: string,
    config?: RequestConfig
  ): Promise<PaginatedApiResponse<T>> {
    const axiosConfig = createRequestConfig(config?.params, {
      headers: config?.headers,
      timeout: config?.timeout,
      signal: config?.signal,
    });
    const response = await apiClient.get<PaginatedApiResponse<T>>(endpoint, axiosConfig);
    return response.data;
  },

  /**
   * POST request - returns the full API response
   */
  async post<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const axiosConfig = createRequestConfig(config?.params, {
      headers: config?.headers,
      timeout: config?.timeout,
      signal: config?.signal,
    });
    const response = await apiClient.post<ApiResponse<T>>(endpoint, data, axiosConfig);
    return response.data;
  },

  /**
   * POST request - returns only the data portion
   */
  async postData<T, D = unknown>(endpoint: string, data?: D, config?: RequestConfig): Promise<T> {
    const response = await this.post<T, D>(endpoint, data, config);
    return response.data;
  },

  /**
   * PUT request - returns the full API response
   */
  async put<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const axiosConfig = createRequestConfig(config?.params, {
      headers: config?.headers,
      timeout: config?.timeout,
      signal: config?.signal,
    });
    const response = await apiClient.put<ApiResponse<T>>(endpoint, data, axiosConfig);
    return response.data;
  },

  /**
   * PUT request - returns only the data portion
   */
  async putData<T, D = unknown>(endpoint: string, data?: D, config?: RequestConfig): Promise<T> {
    const response = await this.put<T, D>(endpoint, data, config);
    return response.data;
  },

  /**
   * PATCH request - returns the full API response
   */
  async patch<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const axiosConfig = createRequestConfig(config?.params, {
      headers: config?.headers,
      timeout: config?.timeout,
      signal: config?.signal,
    });
    const response = await apiClient.patch<ApiResponse<T>>(endpoint, data, axiosConfig);
    return response.data;
  },

  /**
   * PATCH request - returns only the data portion
   */
  async patchData<T, D = unknown>(endpoint: string, data?: D, config?: RequestConfig): Promise<T> {
    const response = await this.patch<T, D>(endpoint, data, config);
    return response.data;
  },

  /**
   * DELETE request - returns the full API response
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    const axiosConfig = createRequestConfig(config?.params, {
      headers: config?.headers,
      timeout: config?.timeout,
      signal: config?.signal,
    });
    const response = await apiClient.delete<ApiResponse<T>>(endpoint, axiosConfig);
    return response.data;
  },

  /**
   * DELETE request - returns only the data portion
   */
  async deleteData<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const response = await this.delete<T>(endpoint, config);
    return response.data;
  },
};

export default apiService;

